/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState } from 'react';

import { GetTokenSilently } from 'interceptors/axios';
import { AuthProviders } from 'modules/auth/config/authConfig';
import { CustomAuthUser } from 'modules/auth/contexts/CustomAuthContext';

import { useAuth0 } from '@auth0/auth0-react';
import { useAuth as useCognito } from 'react-oidc-context';
import { useCustomAuth } from './useCustomAuth';

const AUTH_PROVIDER =
  process.env.REACT_APP_AUTH_PROVIDER?.toLowerCase() || AuthProviders.AUTH0;

// Check if IdP-initiated SAML flow should be used (for Cognito with external IdP like AXA Myriam)
const USE_IDP_INITIATED_SAML =
  process.env.REACT_APP_SAML_IDP_INITIATED === 'true';
const SAML_IDP_URL = process.env.REACT_APP_SAML_IDP_URL || '';
const SAML_SP_ID = process.env.REACT_APP_SAML_SP_ID || '';

interface AuthUser {
  email?: string;
  sub?: string;
  [key: string]: any;
}

interface AuthResponse {
  isAuthenticated: boolean;
  user: AuthUser | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: (logoutParams: any) => Promise<void>; // eslint-disable-line
  getUserAuthData: (user: AuthUser) => AuthUser | null; // eslint-disable-line
  getAccessTokenSilently: () => Promise<GetTokenSilently | null>;
  provider: string;
  // Only for custom auth
  error: string | null;
  clearError: () => void;
  refreshAuth: () => Promise<void>;
}

export const useAuth = (): AuthResponse => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  let auth0: any = null;
  let cognito: any = null;
  let customAuth: any = null;

  if (AUTH_PROVIDER === AuthProviders.AUTH0) {
    auth0 = useAuth0();
  } else if (AUTH_PROVIDER === AuthProviders.COGNITO) {
    cognito = useCognito();
  } else if (AUTH_PROVIDER === AuthProviders.CUSTOM) {
    customAuth = useCustomAuth();
  }

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (AUTH_PROVIDER === AuthProviders.COGNITO) {
          setIsAuthenticated(cognito.isAuthenticated);
          setUser(cognito.user || null);
          setLoading(cognito.isLoading);
        } else if (AUTH_PROVIDER === AuthProviders.AUTH0) {
          setIsAuthenticated(auth0.isAuthenticated);
          setUser(auth0.user);
          setLoading(auth0.isLoading);
        } else if (AUTH_PROVIDER === AuthProviders.CUSTOM) {
          setIsAuthenticated(customAuth.isAuthenticated);
          setUser(customAuth.user);
          setLoading(customAuth.isLoading);
        }
      } catch (error) {
        console.error('Auth check failed:', error); // eslint-disable-line
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
      }
    };

    checkAuth();
  }, [
    auth0?.isAuthenticated,
    auth0?.user,
    auth0?.isLoading,
    cognito?.isAuthenticated,
    cognito?.user,
    cognito?.isLoading,
    customAuth?.isAuthenticated,
    customAuth?.user,
    customAuth?.isLoading,
  ]);

  const login = async (): Promise<void> => {
    if (AUTH_PROVIDER === AuthProviders.COGNITO) {
      // Check if IdP-initiated SAML flow should be used
      if (USE_IDP_INITIATED_SAML && SAML_IDP_URL && SAML_SP_ID) {
        // Build the IdP URL with SPID parameter for IdP-initiated flow
        const url = new URL(SAML_IDP_URL);
        url.searchParams.set('SPID', SAML_SP_ID);

        // Store the current location for post-login redirect
        const returnUrl = window.location.hash.replace('#', '') || '/';
        sessionStorage.setItem('saml_return_url', returnUrl);

        // Redirect to the IdP
        window.location.href = url.toString();
      } else {
        // Use standard SP-initiated flow (generates SAMLRequest)
        await cognito.signinRedirect();
      }
    } else if (AUTH_PROVIDER === AuthProviders.AUTH0) {
      await auth0.loginWithRedirect();
    } else if (AUTH_PROVIDER === AuthProviders.CUSTOM) {
      throw new Error(
        'Login should be handled by the CustomLoginForm component'
      );
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (AUTH_PROVIDER === AuthProviders.COGNITO) {
        const clientId = process.env.REACT_APP_AWS_COGNITO_CLIENT_ID;
        const logoutUri =
          process.env.REACT_APP_DOMAIN || window.location.origin;
        const cognitoDomain = process.env.REACT_APP_AWS_COGNITO_DOMAIN;
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
          logoutUri
        )}`;
        await cognito.removeUser();
      } else if (AUTH_PROVIDER === AuthProviders.AUTH0) {
        await auth0.logout({
          returnTo: window.location.origin,
        });
      } else if (AUTH_PROVIDER === AuthProviders.CUSTOM) {
        await customAuth.logout();
      }
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error); // eslint-disable-line
    }
  };

  const getAccessTokenSilently = async (): Promise<GetTokenSilently | null> => {
    try {
      if (AUTH_PROVIDER === AuthProviders.COGNITO) {
        if (!cognito.user?.access_token && !cognito.isLoading) {
          // If no token and not loading, try to get a new one silently
          await cognito.signinSilent();
        }
        return cognito.user?.access_token || null;
      }
      if (AUTH_PROVIDER === AuthProviders.AUTH0) {
        return await auth0.getAccessTokenSilently();
      }

      // For Custom auth
      return await customAuth.getAccessToken();
    } catch (error) {
      console.error('Get access token error:', error); // eslint-disable-line
      return null;
    }
  };

  const getUserAuthData = (
    user: AuthUser | CustomAuthUser
  ): AuthUser | CustomAuthUser | null => {
    if (AUTH_PROVIDER === AuthProviders.COGNITO) {
      return (user as AuthUser).profile;
    }
    if (AUTH_PROVIDER === AuthProviders.AUTH0) {
      return user as AuthUser;
    }
    if (AUTH_PROVIDER === AuthProviders.CUSTOM) {
      return user as CustomAuthUser;
    }
    return null;
  };

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    getAccessTokenSilently,
    getUserAuthData,
    provider: AUTH_PROVIDER,
    // Only for custom auth
    error: customAuth?.error,
    clearError: customAuth?.clearError,
    refreshAuth: customAuth?.refreshAuth,
  };
};
